/**
 * Cliente da API pública da plataforma HTS (Dashboard/). Site estático, deploy
 * separado — CORS liberado só pra rotas /api/public/* (ver
 * Dashboard/server/public-routes.ts). Base configurável via env de build
 * (VITE_API_BASE_URL); default aponta pro host de produção atual.
 *
 * A allowlist de origem do backend é a env `PUBLIC_SITE_ALLOWED_ORIGINS` (renomeada em
 * 2026-09-10; `WAITLIST_ALLOWED_ORIGINS` segue como fallback) — ver
 * Dashboard/server/public-routes.ts. Os domínios do site já estão lá: este formulário grava
 * lead em produção desde 2026-09-06.
 */
// Default corrigido em 2026-09-10: apontava pra `www.htcapital.com.br`, que NÃO é endereço de
// marketing — é o hostname do gateway, cravado no binário do HTS-desktop (ver Gotchas.md). Na
// prática o `.env.production` sempre sobrepôs, mas um build sem ele publicaria o site apontando
// pra um domínio que está em migração, e falharia em silêncio.
const API_BASE = import.meta.env.VITE_API_BASE_URL || "https://app.htstrader.com.br";
// Chave PÚBLICA do Pagar.me (pk_..., não sk_) — segura de expor no client, é o
// mesmo padrão usado no self-service (my-billing-page.tsx): só permite gerar
// token de cartão, nunca cobrar nem acessar conta. Configurar no build da
// pré-venda quando o backend do Founding Access for pra produção.
const PAGARME_PUBLIC_KEY = import.meta.env.VITE_PAGARME_PUBLIC_KEY || "";
// Pra onde mandar o usuário depois da compra (a própria plataforma, autenticada
// pelo cookie de sessão que o backend já seta na resposta do checkout).
export const APP_BASE_URL = import.meta.env.VITE_APP_BASE_URL || "https://app.htstrader.com.br";

export interface WaitlistPayload {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  referrer?: string;
  consentText?: string;
  /** honeypot — nunca preenchido por humano, mantido vazio no form real */
  website?: string;
}

export interface WaitlistResult {
  ok: boolean;
  status: number;
  error?: string;
}

/**
 * Webhook da SellFlux (CRM do time de marketing). O lead REAL é o que entra na nossa API — é de
 * lá que sai o disparo do link 24h antes, e é lá que existe honeypot, rate limit e histórico.
 * A SellFlux é espelho: se cair, ninguém perde cadastro.
 *
 * Por que `no-cors` no fallback: o endpoint da SellFlux não devolve cabeçalho de CORS pro nosso
 * domínio, então o POST normal com `Content-Type: application/json` estoura no preflight. O
 * fallback reenvia em modo opaco — o navegador manda o corpo, a gente só não consegue ler a
 * resposta (nem precisa: a confirmação do usuário depende da nossa API, não desta).
 */
const SELLFLUX_WEBHOOK_URL =
  import.meta.env.VITE_SELLFLUX_WEBHOOK_URL ||
  "https://webhook.sellflux.app/v2/webhook/custom/736d225815f6cd0c22b27bef66802d1a";

async function mirrorToSellflux(payload: WaitlistPayload): Promise<void> {
  if (!SELLFLUX_WEBHOOK_URL) return;
  const body = JSON.stringify({
    name: [payload.firstName, payload.lastName].filter(Boolean).join(" ") || undefined,
    email: payload.email,
    phone: payload.phone,
    utm_source: payload.utmSource,
    utm_medium: payload.utmMedium,
    utm_campaign: payload.utmCampaign,
    referrer: payload.referrer,
  });
  try {
    const res = await fetch(SELLFLUX_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
    });
    if (res.ok) return;
  } catch {
    // cai no modo opaco abaixo
  }
  try {
    await fetch(SELLFLUX_WEBHOOK_URL, { method: "POST", mode: "no-cors", body });
  } catch {
    // Espelho é best-effort de propósito: o cadastro já está salvo do nosso lado.
  }
}

export async function joinWaitlist(payload: WaitlistPayload): Promise<WaitlistResult> {
  // O espelho sai ANTES da nossa API e não espera por ela. Já esteve amarrado ao `ok` da
  // resposta, e isso criava o pior caso possível: nossa API fora do ar (ou o domínio ainda fora
  // de WAITLIST_ALLOWED_ORIGINS, que é uma pendência real de deploy) e o lead não chegava em
  // lugar NENHUM — nem no banco, nem no CRM do marketing. São dois destinos independentes.
  // Nunca espelha o que caiu no honeypot, senão o spam que a gente filtra aqui reaparece dentro
  // do CRM; e-mail sem "@" também não sai, pra não sujar a base com digitação pela metade.
  if (!payload.website && payload.email.includes("@")) void mirrorToSellflux(payload);

  try {
    const res = await fetch(`${API_BASE}/api/public/waitlist`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (res.status === 429) {
      return { ok: false, status: 429, error: "Muitas tentativas. Tenta de novo em alguns minutos." };
    }
    if (!res.ok) {
      return { ok: false, status: res.status, error: "E-mail inválido — confere e tenta de novo." };
    }
    return { ok: true, status: res.status };
  } catch {
    return { ok: false, status: 0, error: "Não conseguimos conectar agora. Tenta de novo em instantes." };
  }
}

/** Lê UTM/referrer da URL atual, pra anexar no cadastro da waitlist/compra. */
export function captureAttribution(): { utmSource?: string; utmMedium?: string; utmCampaign?: string; referrer?: string } {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get("utm_source") || undefined,
    utmMedium: params.get("utm_medium") || undefined,
    utmCampaign: params.get("utm_campaign") || undefined,
    referrer: document.referrer || undefined,
  };
}

// ── Founding Access — checkout ────────────────────────────────────────────

export interface CardInput {
  number: string;
  holderName: string;
  expMonth: string;
  expYear: string;
  cvv: string;
}

/**
 * Tokeniza o cartão DIRETO no Pagar.me, do navegador — o número cru nunca
 * passa pelo nosso backend (mesmo padrão do self-service, ver
 * Dashboard/client/src/pages/my-billing-page.tsx). Token expira em ~60s,
 * então só gerar na hora de submeter o formulário.
 */
export async function tokenizeCard(card: CardInput): Promise<{ token: string } | { error: string }> {
  if (!PAGARME_PUBLIC_KEY) {
    return { error: "Checkout ainda não configurado (chave pública do Pagar.me ausente)." };
  }
  try {
    const res = await fetch(`https://api.pagar.me/core/v5/tokens?appId=${encodeURIComponent(PAGARME_PUBLIC_KEY)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: "card",
        card: {
          number: card.number.replace(/\s/g, ""),
          holder_name: card.holderName,
          exp_month: Number(card.expMonth),
          exp_year: Number(card.expYear),
          cvv: card.cvv,
        },
      }),
    });
    const json = await res.json();
    if (!res.ok || !json?.id) {
      const fieldErrors = json?.errors && typeof json.errors === "object"
        ? Object.values(json.errors).flat().filter((m): m is string => typeof m === "string")
        : [];
      return { error: fieldErrors.length > 0 ? fieldErrors.join(" ") : (json?.message || "Não foi possível validar o cartão. Confira os dados.") };
    }
    return { token: json.id as string };
  } catch {
    return { error: "Não conseguimos validar o cartão agora. Tenta de novo." };
  }
}

export interface FoundingCheckoutPayload {
  /** 01/10: "novo" cria a conta; "conta" usa a que a pessoa já tem (login + senha). */
  modo: "novo" | "conta";
  /** Uma por envio: reenvio devolve o 1º resultado, e a trava impede duas compras ao mesmo tempo. */
  chaveDaCompra: string;
  email?: string;
  /** Modo "conta": e-mail, CPF ou usuário. */
  login?: string;
  password: string;
  name?: string;
  cpf?: string;
  /** DDD + número. Obrigatório no modo "novo" — a Pagar.me recusa criar o cliente sem telefone. */
  phone?: string;
  cardToken: string;
  /** Caixa da cobrança recorrente marcada e o texto exato que a pessoa viu (gravados antes da cobrança). */
  autorizaCobrancaRecorrente: true;
  textoDaAutorizacao: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  referrer?: string;
  consentText?: string;
  /** Link de parceiro (`/p/<slug>`). O servidor troca a oferta pela do acordo do parceiro. */
  partnerSlug?: string;
}

export interface FoundingCheckoutResult {
  ok: boolean;
  status: number;
  paid?: boolean;
  /** `trialing` = entrou no período de teste e o acesso JÁ está liberado (nada foi cobrado);
   *  `founding_pending` = pagou a pré-venda e espera a onda de ativação. A tela de sucesso é
   *  outra em cada caso — misturar as duas foi o que mostrou "aguardando ativação" a quem já
   *  podia entrar (23/09). */
  subscriptionStatus?: string;
  /** ISO do dia da PRIMEIRA cobrança, só no teste. */
  trialEndsAt?: string | null;
  paymentStatus?: string;
  error?: string;
  fieldErrors?: Record<string, string[]>;
  /** Código estável da recusa: senha_invalida, conta_existente, dados_fiscais, conta_bloqueada, compra_em_andamento. */
  codigo?: string;
  /** dados_fiscais: o que falta (cpf, phone, name) — a tela pede só isso. */
  faltando?: string[];
  /** A compra valeu mas o login automático não abriu: mandar para a tela de entrar. */
  sessaoAberta?: boolean;
  /** Comprou com a conta que já tinha (a tela de sucesso não fala em "senha que acabou de criar"). */
  contaExistente?: boolean;
}

export async function purchaseFounding(payload: FoundingCheckoutPayload): Promise<FoundingCheckoutResult> {
  try {
    const res = await fetch(`${API_BASE}/api/public/founding-checkout`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const json = await res.json().catch(() => ({}));
    if (res.status === 201 || res.status === 402) {
      // 201 = pago; 402 = conta criada mas cartão recusado (retry pela conta, não perdeu a vaga)
      return {
        ok: true, status: res.status, paid: res.status === 201,
        subscriptionStatus: json?.subscriptionStatus, trialEndsAt: json?.trialEndsAt ?? null,
        paymentStatus: json?.paymentStatus, sessaoAberta: json?.sessaoAberta, contaExistente: json?.contaExistente,
      };
    }
    if (res.status === 429) {
      return { ok: false, status: 429, error: "Muitas tentativas. Tenta de novo em 1 hora, ou fala com o suporte." };
    }
    if (res.status === 400) {
      return { ok: false, status: 400, error: json?.error || "Dados inválidos.", fieldErrors: json?.details };
    }
    // 401 senha, 403 bloqueada, 409 conta existente/compra em andamento, 422 dado faltando: o código decide a tela.
    if ([401, 403, 409, 422].includes(res.status)) {
      return { ok: false, status: res.status, error: json?.error || "Não foi possível concluir a compra.", codigo: json?.codigo, faltando: json?.faltando };
    }
    return { ok: false, status: res.status, error: json?.error || "Não foi possível processar sua compra agora. Tenta de novo." };
  } catch {
    return { ok: false, status: 0, error: "Não conseguimos conectar agora. Tenta de novo em instantes." };
  }
}

export interface FoundingRemaining {
  totalSeats: number;
  remaining: number;
  /** Preço ATUAL do plano, em centavos (`plans.priceCents` — o mesmo campo que
   *  `purchaseFoundingAccess` trava em `priceSnapshotCents` na hora da compra). É o valor
   *  REALMENTE cobrado agora — nunca escrever preço fixo na página sem vir daqui, senão o
   *  texto pode dizer uma coisa e a API cobrar outra (ex.: lote 2 sobe pra R$189 no banco e a
   *  página continua dizendo R$139 até alguém lembrar de trocar o texto). */
  priceCents: number;
  /** 1 = Primeiro Lote, 2 = Segundo, 3 = Terceiro... (`founding_access_config.currentLote`,
   *  incrementado pelo admin ao ativar um lote novo). A página usa isto pra virar o rótulo
   *  ("Primeiro/Segundo/Terceiro Lote") sozinha — nunca hardcodar o número do lote no texto. */
  loteNumber: number;
}

/** Vagas + preço + número do lote atual — SEMPRE os valores reais (`server/payments/
 *  person-billing.ts`, `getFoundingAccessMetrics`, derivado ao vivo de `subscriptions`/`plans`/
 *  `founding_access_config`). Nunca hardcode um valor aqui: contador falso é descoberto — alguém
 *  abre em duas abas e compara, e preço errado é dinheiro errado. `null` = ainda não sabemos
 *  (carregando ou a consulta falhou) — quem usa isso decide o que mostrar nesse caso, nunca
 *  inventa um valor. */
export async function getFoundingRemaining(): Promise<FoundingRemaining | null> {
  try {
    const res = await fetch(`${API_BASE}/api/public/founding-remaining`);
    if (!res.ok) return null;
    const json = await res.json();
    if (
      typeof json?.remaining !== "number" ||
      typeof json?.totalSeats !== "number" ||
      typeof json?.priceCents !== "number" ||
      typeof json?.loteNumber !== "number"
    ) return null;
    return { totalSeats: json.totalSeats, remaining: json.remaining, priceCents: json.priceCents, loteNumber: json.loteNumber };
  } catch {
    return null;
  }
}

/** Bloco do texto da condição — espelho de `BlocoDaCondicao` em `Dashboard/shared/texto-da-condicao.ts` (o site não
 *  importa o shared do app). Dados, nunca HTML: a página monta os elementos (§S1). */
export type BlocoDaCondicao =
  | { tipo: "titulo"; texto: string }
  | { tipo: "subtitulo"; texto: string }
  | { tipo: "paragrafo"; trechos: { texto: string; negrito?: boolean }[] }
  | { tipo: "lista"; itens: { rotulo: string; texto: string }[] };

/** Oferta de um link de parceiro (`/p/<slug>`). Preço SEMPRE daqui — é o mesmo acordo que o
 *  checkout cobra (`resolverOfertaDeParceiro` no Dashboard), nunca texto fixo na página. */
export interface PartnerOffer {
  slug: string;
  partnerName: string;
  campaign: string | null;
  planName: string;
  priceCents: number;
  fullPriceCents: number | null;
  /** Dias de teste da condição comercial. 0 = cobra na hora e entra na fila de ativação. */
  trialDays?: number;
  /** Meta MÍNIMA de lotes por ciclo do acordo (18/10/2026). Todo ciclo que bater a meta ganha o benefício.
   *  `null`/ausente = acordo sem meta (servidor antigo também não manda). */
  metaLotes?: number | null;
  /** Benefício de quem bate a meta: isenção do ciclo ou desconto de N%. */
  beneficio?: { tipo: "isencao" | "desconto"; descontoPct: number | null } | null;
  /** Corretoras cujas contas contam para a meta, já por extenso ("XP", "XP ou Genial"). Só lotes executados pelo
   *  HTS em conta delas contam (18/10/2026). `null`/ausente = acordo sem meta. */
  corretorasTexto?: string | null;
  /** Texto da condição, montado no servidor (`textoDaCondicao`, o modelo do dono de 18/10). O aceite grava este
   *  texto EXATO — redigir de novo aqui seria uma segunda promessa para a mesma regra. */
  condicao?: string | null;
  /** A mesma condição em blocos (título, parágrafos, regras), pronta do servidor: a página só monta os elementos.
   *  `null`/ausente = sem meta, ou servidor antigo (a página cai no `condicao` em texto). */
  condicaoBlocos?: BlocoDaCondicao[] | null;
  /** Página de campanha montada para este parceiro (22/09/2026). Quando vem, esta página manda o
   *  visitante para lá — é a convivência entre a página fixa daqui e o editor de campanhas. */
  landingUrl?: string | null;
}

/** `"encerrado"` = link inexistente, desativado ou parceiro sem acordo (404). `null` = falha de
 *  rede — a página diz "tente de novo" em vez de afirmar que o link acabou. */
export async function getPartnerOffer(slug: string): Promise<PartnerOffer | "encerrado" | null> {
  try {
    const res = await fetch(`${API_BASE}/api/public/partner-offer/${encodeURIComponent(slug)}`);
    if (res.status === 404) return "encerrado";
    if (!res.ok) return null;
    const json = await res.json();
    if (typeof json?.priceCents !== "number" || typeof json?.partnerName !== "string") return null;
    return json as PartnerOffer;
  } catch {
    return null;
  }
}
